import { assign, createMachine } from "xstate";
import dayjs from "dayjs";
import apiMethods from "@/js/api/apiMethods";
import customParseFormat from "dayjs/plugin/customParseFormat";

dayjs.extend(customParseFormat);

let defaultContext = {
	date: "",
	dateError: "",
	number: "",
	orderNumberError: "",
	image: "",
	imageError: "",
	goods: [], // Массив с маслами
	errors: [], // ошибки сервера
	debug: [], // Массив для логов
};

export const uploadOrderMachine = createMachine(
	{
		id: "upload-order",
		type: "parallel",
		context: { ...defaultContext },
		states: {
			type: {
				initial: "photo",
				states: {
					photo: {
						entry: ["scrollTop"],
						on: {
							TOGGLE: {
								actions: ["resetErrors"],
								target: "fields",
							},
						},
					},
					fields: {
						entry: ["scrollTop"],
						on: {
							TOGGLE: {
								actions: ["resetErrors"],
								target: "photo",
							},
						},
					},
				},
			},
			form: {
				initial: "idle",
				states: {
					idle: {
						on: {
							INPUT: {
								actions: ["saveData"],
								internal: true,
							},
							SET_GOODS: {
								actions: ["saveGoods"],
								internal: true,
							},
							SUBMIT_FIELDS: {
								actions: ["resetErrors"],
								target: "validating_fields",
							},
						},
					},
					validating_fields: {
						invoke: {
							id: "validate_fields",
							src: "validate_fields",
						},
						on: {
							VALID: {
								target: "sending_fields",
							},
							INVALID: {
								actions: ["saveErrors"],
								target: "show_errors",
							},
						},
					},
					show_errors: {
						entry: ["scrollTop"],
						on: {
							INPUT: {
								actions: ["saveData"],
								internal: true,
							},
							SET_GOODS: {
								actions: ["saveGoods"],
								internal: true,
							},
							SUBMIT_FIELDS: {
								actions: ["resetErrors"],
								target: "validating_fields",
							},
						},
					},
					sending_fields: {
						invoke: {
							id: "sendOrder_fields",
							src: "sendOrder_fields",
						},
						on: {
							DONE_FIELDS: {
								target: "success",
							},
							ERROR_FIELDS: {
								target: "failure",
								actions: ["setServerError"],
							},
						},
					},
					success: {
						entry: ["resetContext", "scrollTop"],
						on: {
							ONE_MORE: {
								target: "idle",
							},
						},
					},
					failure: {
						on: {
							INPUT: {
								actions: ["saveData"],
								internal: true,
							},
							SET_GOODS: {
								actions: ["saveGoods"],
								internal: true,
							},
							SUBMIT_FIELDS: {
								actions: ["resetErrors"],
								target: "validating_fields",
							},
						},
					},
				},
			},
		},
	},
	{
		actions: {
			saveData: assign((ctx, message) => message.data),
			saveGoods: assign({
				goods: (ctx, message) => message.data.goods,
			}),
			addDebugLog: assign({
				debug: (ctx, message) => [
					...ctx.debug,
					{
						timestamp: new Date().toISOString(),
						type: message.logType,
						data: message.logData,
						state: message.state,
					},
				],
			}),
			saveErrors: assign((ctx, message) => message.data),
			resetErrors: assign({
				dateError: "",
				orderNumberError: "",
				imageError: "",
				errors: [],
			}),
			setServerError: assign({
				errors: (ctx, message) => {
					return Array.isArray(message.data?.errors)
						? Array.from(message.data.errors)
						: ([message.data?.message] ?? ["Ошибка сервера!"]);
				},
			}),
			resetContext: assign({
				date: "",
				dateError: "",
				number: "",
				orderNumberError: "",
				image: "",
				imageError: "",
				goods: [],
				errors: [],
			}),
			scrollTop: () => {
				window.scrollTo(0, 0);
			},
		},
		services: {
			validate_fields: ctx => send => {
				let date = validateDate(ctx.date);
				let orderNumber = validateOrderNumber(ctx.number);
				let goods = validateOils(ctx.goods);

				let errors = {
					dateError: "",
					orderNumberError: "",
					imageError: "",
					goodsErrors: {},
				};

				if (!ctx.image) {
					errors.imageError = "Поле не может быть пустым";
				}

				if (date !== "ok") {
					errors.dateError = date;
				}

				if (orderNumber !== "ok") {
					errors.orderNumberError = orderNumber;
				}

				if (goods !== "ok") {
					errors.goodsErrors = goods;
				}

				// Проверяем, есть ли ошибки
				let haveErrors =
					errors.dateError !== "" ||
					errors.orderNumberError !== "" ||
					errors.imageError !== "" ||
					Object.keys(errors.goodsErrors).length > 0;

				if (haveErrors) {
					send({
						type: "INVALID",
						data: errors,
					});
				} else {
					send({ type: "VALID" });
				}
			},
			sendOrder_fields: (ctx, message, meta) => async send => {
				// Лог: начало отправки
				console.log("📤 [ORDER] Начало отправки заказ-наряда", {
					number: ctx.number,
					date: ctx.date,
					oils_count: ctx.goods?.length,
					images_count: ctx.image?.length,
				});

				let formData = new FormData();

				// Лог: добавление изображений
				if (ctx.image && ctx.image.length > 0) {
					console.log(`🖼️ [ORDER] Добавлено изображений: ${ctx.image.length}`);
					for (let i = 0; i < ctx.image.length; i++) {
						formData.append("photos[]", ctx.image[i]);
						console.log(
							`  - Файл ${i + 1}: ${ctx.image[i].name}, ${(ctx.image[i].size / 1024).toFixed(2)} KB`,
						);
					}
				}

				// Лог: основные поля
				formData.append("number", ctx.number);
				formData.append("creation_date", ctx.date);
				console.log("📝 [ORDER] Основные поля:", {
					number: ctx.number,
					creation_date: ctx.date,
				});

				// Лог: масла
				if (ctx.goods && ctx.goods.length > 0) {
					const transformedGoods = ctx.goods.map(good => ({
						...good,
						container_type:
							good.container_type && good.container_type.startsWith("canister_")
								? good.container_type
								: good.container_type === "pouring"
									? "pouring"
									: good.container_type,
					}));

					const oilsJson = JSON.stringify(transformedGoods);
					formData.append("goods", oilsJson);
					console.log(
						"🛢️ [ORDER] Масла (после преобразования):",
						transformedGoods,
					);
				}

				// Лог: полный FormData (что можно вывести)
				console.log("📦 [ORDER] FormData entries:");
				for (let pair of formData.entries()) {
					if (pair[0] === "photos[]") {
						console.log(`  ${pair[0]}: [File] ${pair[1].name}`);
					} else {
						console.log(`  ${pair[0]}: ${pair[1]}`);
					}
				}

				await apiMethods.uploadOrder
					.postData(formData)
					.then(res => {
						console.log("✅ [ORDER] Успешная отправка:", res.data);
						send({ type: "DONE_FIELDS" });
					})
					.catch(e => {
						console.error("❌ [ORDER] Ошибка отправки:", {
							status: e.response?.status,
							statusText: e.response?.statusText,
							data: e.response?.data,
							message: e.message,
						});
						send({
							type: "ERROR_FIELDS",
							data: e.response?.data || {
								message: e.message || "Ошибка при отправке",
							},
						});
					});
			},
		},
	},
);

// Вспомогательные функции валидации
function validateDate(value) {
	if (!value) {
		return "Поле не может быть пустым.";
	}

	let parsedDate = dayjs(value, "DD.MM.YYYY", "ru", true);

	if (parsedDate.isValid()) {
		return "ok";
	} else {
		return "Поле заполнено неверно. Формат: 01.01.2022";
	}
}

function validateOrderNumber(value) {
	if (!value) {
		return "Поле не может быть пустым";
	} else {
		return "ok";
	}

	let format = /^\d{3}-\d{3}-\d{3}$/;

	if (format.test(value)) {
		return "ok";
	} else {
		return "Поле заполнено неверно. Формат: 123-456-789";
	}
}

function validateOils(oils) {
	if (!oils || oils.length === 0) {
		return { general: "Добавьте хотя бы одно масло" };
	}

	const errors = {};

	oils.forEach((oil, index) => {
		const oilErrors = {};

		if (!oil.oil_name) {
			oilErrors.oil_name = "Выберите масло";
		}

		if (!oil.container_type) {
			oilErrors.container_type = "Выберите тип тары";
		}

		if (!oil.quantity || parseFloat(oil.quantity) <= 0) {
			oilErrors.quantity = "Введите корректное количество литров";
		}

		if (!oil.sum || parseFloat(oil.sum) <= 0) {
			oilErrors.sum = "Введите корректную сумму";
		}

		if (Object.keys(oilErrors).length > 0) {
			errors[index] = oilErrors;
		}
	});

	return Object.keys(errors).length > 0 ? errors : "ok";
}
